import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BadgeIcon from '@mui/icons-material/Badge';
import CakeIcon from '@mui/icons-material/Cake';
import DescriptionIcon from '@mui/icons-material/Description';
import FemaleIcon from '@mui/icons-material/Female';
import HistoryIcon from '@mui/icons-material/History';
import MaleIcon from '@mui/icons-material/Male';
import PublicIcon from '@mui/icons-material/Public';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useTranslation } from 'react-i18next';
import api, { apiBaseURL } from '../../services/api';
import CountryFlagLabel from '../../components/CountryFlagLabel';
import { SMART_COLORS as COLORS, smartCardSx } from '../../theme/smartStableTheme';
import { formatDateDisplay } from '../../utils/dateUtils';

function cleanPedigree(html) {
  return html
    ? html
        .replace(/<div class="info-line text-center">.*?<\/div>/gs, '')
        .replace(/<div class="text-center text-small">.*?<\/div>/gs, '')
    : '';
}

function formatDate(value) {
  return formatDateDisplay(value);
}

function calculateAge(value) {
  if (!value) return null;
  const birth = new Date(value);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  if (
    today.getMonth() < birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
  ) {
    age -= 1;
  }
  return Math.max(age, 0);
}

function ageLabel(age, t) {
  if (age === null) return '-';
  return t('clientHorseDetails.ageYears', { count: age });
}

function genderLabel(gender, age, t) {
  if (gender === 'M') return age !== null && age < 3 ? t('clientHorseDetails.filly') : t('clientHorseDetails.mare');
  if (gender === 'S') return age !== null && age < 3 ? t('clientHorseDetails.colt') : t('clientHorseDetails.stallion');
  return gender || '-';
}

function localizedPair(en, ar, lang) {
  return String(lang || '').startsWith('ar') ? ar || en || '-' : en || ar || '-';
}

function assetUrl(value) {
  if (!value) return undefined;
  if (value.startsWith('data') || value.startsWith('http')) return value;
  return `${apiBaseURL}/uploads/${value}`;
}

function InfoCard({ title, children }) {
  return (
    <Paper elevation={0} sx={{ ...smartCardSx, p: 2.2 }}>
      <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900, mb: 1.5 }}>
        {title}
      </Typography>
      <Stack spacing={0.5}>{children}</Stack>
    </Paper>
  );
}

function DetailRow({ label, value }) {
  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="space-between"
      gap={1}
      sx={{ py: 1.1, borderBottom: `1px solid ${COLORS.line}` }}
    >
      <Typography sx={{ color: COLORS.muted, fontWeight: 800 }}>{label}</Typography>
      <Typography sx={{ color: COLORS.text, fontWeight: 900, textAlign: { xs: 'start', sm: 'end' } }}>
        {value || '-'}
      </Typography>
    </Stack>
  );
}

function SummaryCard({ icon, label, value, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...smartCardSx, p: 2, minHeight: 110 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar sx={{ bgcolor: soft, color, width: 52, height: 52 }}>{icon}</Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ color, fontWeight: 900 }}>
            {label}
          </Typography>
          <Typography noWrap sx={{ color: COLORS.text, fontWeight: 900, fontSize: 20 }}>
            {value || '-'}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

const HorseDetailsClient = () => {
  const { horseId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRtl = String(i18n.language || '').startsWith('ar');
  const lang = isRtl ? 'ar' : 'en';
  const [horse, setHorse] = useState(null);
  const [lookups, setLookups] = useState({
    ownerName: '',
    color: null,
    breed: null,
    country: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const { data: horseData } = await api.get(`/horses/${horseId}`);
        if (cancelled) return;
        setHorse(horseData);

        const [colorRes, breedRes, countryRes, ownerRes] = await Promise.allSettled([
          horseData.color_id ? api.get(`/horse_colors/${horseData.color_id}`) : Promise.resolve({ data: null }),
          horseData.breed_id ? api.get(`/horse_breeds/${horseData.breed_id}`) : Promise.resolve({ data: null }),
          horseData.country_of_origin_id ? api.get(`/countries/${horseData.country_of_origin_id}`) : Promise.resolve({ data: null }),
          horseData.owner_id ? api.get(`/owners/${horseData.owner_id}`) : Promise.resolve({ data: null }),
        ]);

        if (cancelled) return;
        const owner = ownerRes.status === 'fulfilled' ? ownerRes.value.data : null;
        setLookups({
          color: colorRes.status === 'fulfilled' ? colorRes.value.data : null,
          breed: breedRes.status === 'fulfilled' ? breedRes.value.data : null,
          country: countryRes.status === 'fulfilled' ? countryRes.value.data : null,
          ownerName: [owner?.first_name, owner?.last_name].filter(Boolean).join(' '),
        });
      } catch (err) {
        if (!cancelled) setError(t('clientHorseDetails.loadError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [horseId, t]);

  const age = useMemo(() => calculateAge(horse?.date_of_birth), [horse]);
  const pedigreeHtml = useMemo(() => cleanPedigree(horse?.pedigree_html), [horse]);
  const colorName = localizedPair(lookups.color?.color_english, lookups.color?.color_arabic, lang);
  const breedName = localizedPair(lookups.breed?.breed_english, lookups.breed?.breed_arabic, lang);
  const countryName = localizedPair(lookups.country?.country_english, lookups.country?.country_arabic, lang);
  const displayName = localizedPair(horse?.name, horse?.name_arabic, lang);

  if (loading) {
    return (
      <Box
        className="smart-page-container"
        dir={isRtl ? 'rtl' : 'ltr'}
        sx={{ maxWidth: 'var(--app-page-max)', mx: 'auto', minHeight: '55vh', display: 'grid', placeItems: 'center' }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress size={28} />
          <Typography sx={{ color: COLORS.muted, fontWeight: 800 }}>{t('clientHorseDetails.loading')}</Typography>
        </Stack>
      </Box>
    );
  }

  if (error || !horse) {
    return (
      <Box className="smart-page-container" dir={isRtl ? 'rtl' : 'ltr'} sx={{ maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
        <Alert severity="error">{error || t('clientHorseDetails.notFound')}</Alert>
      </Box>
    );
  }

  return (
    <Box className="smart-page-container" dir={isRtl ? 'rtl' : 'ltr'} sx={{ maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          ...smartCardSx,
          p: { xs: 2.5, md: 3 },
          mb: 2.5,
          background: 'linear-gradient(135deg, #fffdf8 0%, #f4efe5 100%)',
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2.5}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'center', sm: 'center' }}>
            <Avatar
              src={assetUrl(horse.image)}
              alt={horse.name}
              sx={{
                width: { xs: 122, sm: 148 },
                height: { xs: 122, sm: 148 },
                bgcolor: COLORS.goldSoft,
                color: COLORS.gold,
                fontSize: 42,
                fontWeight: 900,
                border: `4px solid ${COLORS.card}`,
                boxShadow: '0 12px 28px rgba(91,75,39,0.12)',
              }}
            >
              {displayName.charAt(0)}
            </Avatar>
            <Box sx={{ textAlign: { xs: 'center', sm: 'start' } }}>
              <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>
                {displayName}
              </Typography>
              {horse.name && horse.name_arabic && (
                <Typography variant="h5" sx={{ color: COLORS.gold, fontWeight: 900, mt: 0.4, direction: 'ltr' }}>
                  {lang === 'ar' ? horse.name : horse.name_arabic}
                </Typography>
              )}
              <Typography sx={{ color: COLORS.muted, mt: 0.7 }}>
                {t('clientHorseDetails.subtitle')}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent={{ xs: 'center', sm: 'flex-start' }} sx={{ mt: 1.4 }}>
                <Chip
                  size="small"
                  icon={horse.gender === 'S' ? <MaleIcon /> : <FemaleIcon />}
                  label={genderLabel(horse.gender, age, t)}
                  sx={{ bgcolor: horse.gender === 'S' ? COLORS.blueSoft : COLORS.roseSoft, color: COLORS.text, fontWeight: 900 }}
                />
                <Chip
                  size="small"
                  icon={<QrCode2Icon />}
                  label={horse.rfid_chip_number || `${t('clientHorseDetails.rfid')} -`}
                  sx={{ bgcolor: COLORS.card, color: COLORS.text, fontWeight: 900 }}
                />
              </Stack>
            </Box>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="outlined"
              startIcon={<TimelineIcon />}
              onClick={() => navigate(`/horse-details/${horse.horse_id}/production-timeline`)}
              sx={{ borderColor: COLORS.line, color: COLORS.text, fontWeight: 900 }}
            >
              {t('clientHorseDetails.productionTimeline')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => navigate(`/HorseHistory/${horse.horse_id}`)}
              sx={{ borderColor: COLORS.line, color: COLORS.text, fontWeight: 900 }}
            >
              {t('clientHorseDetails.serviceHistory')}
            </Button>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}
            >
              {t('back')}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 2, mb: 2.5 }}>
        <SummaryCard icon={<CakeIcon />} label={t('age')} value={ageLabel(age, t)} />
        <SummaryCard icon={<BadgeIcon />} label={t('owner')} value={lookups.ownerName || '-'} color={COLORS.blue} soft={COLORS.blueSoft} />
        <SummaryCard
          icon={<PublicIcon />}
          label={t('country_of_origin')}
          value={<CountryFlagLabel country={countryName} />}
          color={COLORS.green}
          soft={COLORS.greenSoft}
        />
        <SummaryCard icon={<DescriptionIcon />} label={t('registration_number')} value={horse.registration_number || '-'} color={COLORS.rose} soft={COLORS.roseSoft} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 2, mb: 2.5 }}>
        <InfoCard title={t('clientHorseDetails.identity')}>
          <DetailRow label={t('clientHorseDetails.rfid')} value={horse.rfid_chip_number} />
          <DetailRow label={t('gender')} value={genderLabel(horse.gender, age, t)} />
          <DetailRow label={t('date_of_birth')} value={<span className="date-ltr">{formatDate(horse.date_of_birth)}</span>} />
          <DetailRow label={t('color')} value={colorName} />
          <DetailRow label={t('breed')} value={breedName} />
        </InfoCard>

        <InfoCard title={t('clientHorseDetails.lineageOwnership')}>
          <DetailRow label={t('father_name')} value={localizedPair(horse.father_name, horse.father_name_arabic, lang)} />
          <DetailRow label={t('mother_name')} value={localizedPair(horse.mother_name, horse.mother_name_arabic, lang)} />
          <DetailRow label={t('breeder_name')} value={localizedPair(horse.breeder_name, horse.breeder_name_arabic, lang)} />
          <DetailRow label={t('owner')} value={lookups.ownerName} />
        </InfoCard>

        <InfoCard title={t('clientHorseDetails.registrationDocuments')}>
          <DetailRow label={t('registration_number')} value={horse.registration_number} />
          <DetailRow label={t('registration_date')} value={<span className="date-ltr">{formatDate(horse.registration_date)}</span>} />
          <Divider sx={{ borderColor: COLORS.line, my: 1 }} />
          {horse.registration_certificate ? (
            <Button
              variant="contained"
              startIcon={<DescriptionIcon />}
              href={assetUrl(horse.registration_certificate)}
              target="_blank"
              rel="noopener noreferrer"
              download={`${horse.name || 'horse'}_registration_certificate.pdf`}
              sx={{ bgcolor: COLORS.gold, fontWeight: 900, '&:hover': { bgcolor: COLORS.goldHover } }}
            >
              {t('clientHorseDetails.downloadCertificate')}
            </Button>
          ) : (
            <Chip label={t('clientHorseDetails.noCertificate')} sx={{ bgcolor: COLORS.cardSoft, color: COLORS.muted, fontWeight: 900 }} />
          )}
        </InfoCard>
      </Box>

      <Paper elevation={0} sx={{ ...smartCardSx, p: 2.2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} sx={{ mb: 1.5 }}>
          <Box>
            <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900 }}>
              {t('pedigree')}
            </Typography>
            <Typography variant="caption" sx={{ color: COLORS.muted }}>
              {t('clientHorseDetails.pedigreeHint')}
            </Typography>
          </Box>
          <Tooltip title={t('clientHorseDetails.serviceHistory')}>
            <IconButton onClick={() => navigate(`/HorseHistory/${horse.horse_id}`)} sx={{ color: COLORS.gold }}>
              <HistoryIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        {pedigreeHtml ? (
          <Box
            sx={{
              border: `1px solid ${COLORS.line}`,
              borderRadius: 2,
              bgcolor: COLORS.cardSoft,
              p: 2,
              overflowX: 'auto',
              '& table': { mx: 'auto' },
            }}
            dangerouslySetInnerHTML={{ __html: pedigreeHtml }}
          />
        ) : (
          <Box sx={{ p: 4, textAlign: 'center', color: COLORS.muted, bgcolor: COLORS.cardSoft, borderRadius: 2 }}>
            {t('clientHorseDetails.noPedigree')}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default HorseDetailsClient;
